/* The connector catalogue: what this deployment can do with each source family, and what to ask for.
 *
 * Read once and cached for the session. It describes the running deployment rather than any tenant's
 * data, so it changes only when somebody redeploys — refetching it on every mount would be a request
 * per form open for an answer that cannot have moved.
 *
 * **The form is generated from this rather than hand-written**, and that is the point. Five providers
 * want five different field sets and one of them is a secret; a hand-written form per provider is how
 * a field comes to be asked for and silently ignored, or required by the API and absent from the
 * screen. `secret_fields` in particular is read rather than guessed: matching on the word "password"
 * would render a Databricks `token` as plain text.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isConnectorProfile } from '@/shared/api/generated/catalog';
import type { ConnectorProfile } from '@/shared/api/generated/catalog';
import { getPage } from '@/shared/api/http';
import type { Page } from '@/shared/api/http';

export const CONNECTORS_URL = '/v1/catalog/connectors';

export function useConnectors(): UseQueryResult<Page<ConnectorProfile>> {
  return useQuery({
    queryKey: ['catalog', 'connectors'] as const,
    queryFn: () => getPage(CONNECTORS_URL, isConnectorProfile, 'connector profile'),
    /* An hour, not `Infinity`: a long-lived tab across a deploy should eventually see the new
     * deployment's capabilities, and an hour is short enough for that while removing the per-mount
     * request entirely. */
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}
